import { useEffect, useState } from 'react'
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material'
import axios from 'axios'
import { useDebounce } from '../../../../utils/searchDebouncer'
import { getDeclaredLanguages } from '../../../Applicants/utils/formatDataForTable'

export default function RecruitmentCandidates({
  candidates,
  previousCandidates = [],
  onChange,
  canSelectApplicants,
  disabled,
  requiredCount
}) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search.trim(), 300)
  const [options, setOptions] = useState([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [manualName, setManualName] = useState('')
  const normalizeName = (name) => (name || '').trim().replace(/\s+/g, ' ').toLowerCase()
  const existingIds = new Set(previousCandidates.filter((candidate) => candidate.applicant_id).map((candidate) => candidate.applicant_id))
  const allCandidates = [...previousCandidates, ...candidates]
  const duplicateTypedName =
    manualName.trim() && allCandidates.some((candidate) => normalizeName(candidate.name) === normalizeName(manualName))
  const duplicateSelectedName = candidates.some(
    (candidate, index) =>
      !candidate.applicant_id &&
      [...previousCandidates, ...candidates.filter((_, otherIndex) => otherIndex !== index)].some(
        (other) => normalizeName(other.name) === normalizeName(candidate.name)
      )
  )

  useEffect(() => {
    let active = true
    setError('')
    if (!canSelectApplicants || search.trim().length < 3) {
      setOptions([])
      setHasMore(false)
      setLoading(false)
      return () => {
        active = false
      }
    }
    if (search.trim() !== debouncedSearch) {
      setLoading(true)
      return () => {
        active = false
      }
    }
    setLoading(true)
    const fetchApplicants = async () => {
      try {
        const { data } = await axios.get('/staffingRequest/applicants', { params: { search: debouncedSearch, page } })
        if (!active) return
        if (!data.success) throw new Error(data.message)
        setOptions((previous) => (page === 0 ? data.data : [...previous, ...data.data]))
        setHasMore(data.hasMore)
      } catch (err) {
        if (active) setError('Kërkimi i aplikantëve dështoi. Provoni përsëri.')
      } finally {
        if (active) setLoading(false)
      }
    }
    fetchApplicants()
    return () => {
      active = false
    }
  }, [search, debouncedSearch, page, canSelectApplicants])

  const getStatusLabel = (applicant) => {
    if (applicant.status !== 'new') return applicant.status
    if (!applicant.created_at) return ''
    const applicationAge = Date.now() - new Date(applicant.created_at).getTime()
    return applicationAge >= 0 && applicationAge <= 15 * 24 * 60 * 60 * 1000 ? 'New' : ''
  }

  const addManualCandidate = () => {
    if (!manualName.trim() || candidates.length >= 100) return
    onChange([...candidates, { name: manualName.trim() }])
    setManualName('')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { border: '1px solid #9ca3af' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: 2 },
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' }
      }}
    >
      <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
        Kandidatët e rekrutuar
      </Typography>
      <Typography variant='body2' color={candidates.length === requiredCount ? 'text.secondary' : 'error.main'} role='status'>
        {candidates.length} / {requiredCount} kandidatë të zgjedhur. Numri duhet të përputhet me personat që po shtohen në rekrutim.
      </Typography>
      {canSelectApplicants && (
        <>
          <Autocomplete
            value={null}
            inputValue={search}
            options={options.filter((option) => !candidates.some((candidate) => candidate.applicant_id === option._id))}
            filterOptions={(items) => items}
            getOptionDisabled={(option) => existingIds.has(option._id)}
            getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''}`.trim()}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            loading={loading}
            loadingText='Duke kërkuar aplikantë…'
            disabled={disabled || candidates.length >= 100}
            onInputChange={(_, value) => {
              setSearch(value)
              setPage(0)
              setOptions([])
              setHasMore(false)
            }}
            onChange={(_, applicant) => {
              if (!applicant || existingIds.has(applicant._id)) return
              onChange([
                ...candidates,
                {
                  applicant_id: applicant._id,
                  name: `${applicant.firstName || ''} ${applicant.lastName || ''}`.trim()
                }
              ])
              setSearch('')
              setPage(0)
            }}
            noOptionsText={search.trim().length < 3 ? 'Shkruani të paktën 3 shkronja' : 'Nuk u gjetën aplikantë'}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant='body2'>
                      {option.firstName} {option.lastName}
                    </Typography>
                    {getStatusLabel(option) === 'New' && (
                      <Chip
                        label='New'
                        size='small'
                        sx={{ height: 22, bgcolor: '#e0f2fe', color: '#075985', fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    )}
                    {existingIds.has(option._id) && <Typography variant='caption'>— Tashmë në këtë kërkesë</Typography>}
                  </Box>
                  <Typography variant='caption'>
                    {[
                      option.position,
                      option.status !== 'new' ? option.status : '',
                      option.office_location,
                      getDeclaredLanguages(option.languageAbility)
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                variant='outlined'
                label='Kërko aplikant'
                placeholder='Emri ose mbiemri'
                helperText='Shkruani të paktën 3 shkronja për të kërkuar.'
                inputProps={{ ...params.inputProps, maxLength: 100 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color='inherit' size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          {hasMore && (
            <Button size='small' disabled={loading || disabled} onClick={() => setPage((value) => value + 1)}>
              Shfaq më shumë aplikantë
            </Button>
          )}
          {error && (
            <Alert severity='error' sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </>
      )}
      {(duplicateTypedName || duplicateSelectedName) && (
        <Alert severity='warning' sx={{ mt: 1 }}>
          Ky emër është shtuar tashmë në këtë kërkesë ose në këtë përditësim. Nëse është person tjetër me të njëjtin emër, mund të vazhdoni.
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextField
          variant='outlined'
          label='Emri i kandidatit të rekomanduar'
          size='small'
          fullWidth
          value={manualName}
          disabled={disabled || candidates.length >= 100}
          inputProps={{ maxLength: 300 }}
          onChange={(event) => setManualName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              addManualCandidate()
            }
          }}
          helperText={manualName.trim() ? 'Klikoni Shto për ta përfshirë në këtë rekrutim.' : ''}
        />
        <Button
          variant='outlined'
          sx={{ minHeight: 40, minWidth: 80, flexShrink: 0 }}
          disabled={disabled || !manualName.trim() || candidates.length >= 100}
          onClick={addManualCandidate}
        >
          Shto
        </Button>
      </Box>
      {candidates.length > 0 && (
        <Typography variant='caption' color='text.secondary'>
          {candidates.length} kandidatë të zgjedhur
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {candidates.map((candidate, index) => (
          <Chip
            key={candidate.applicant_id || `manual-${index}`}
            label={`${candidate.name}${!candidate.applicant_id ? ' (manual)' : ''}`}
            disabled={disabled}
            onDelete={() => onChange(candidates.filter((_, candidateIndex) => candidateIndex !== index))}
            sx={{ maxWidth: '100%' }}
          />
        ))}
      </Box>
    </Box>
  )
}
